import React, { useEffect, useCallback, useMemo, useState, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import { IconPlus, IconSearch } from './Icons';
import { sendEmail } from '../services/emailService';
import {
  buildProfessionalAnnouncementHtml,
  buildAnnouncementPlainText,
} from '../utils/announcementEmailHtml';
import AnnouncementUnlayerEditors, {
  exportFromEmailEditorRef,
} from './AnnouncementUnlayerEditors';
import './TeamAnnouncements.css';

const TEMPLATE_TAB = {
  visual: 'visual',
  code: 'code',
  defaults: 'defaults',
};

const SAMPLE_HEADER_HTML = `<table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="background:#0f172a;border-radius:8px 8px 0 0;margin:0">
  <tr>
    <td style="padding:18px 22px;color:#f8fafc;font-size:18px;font-weight:600;letter-spacing:0.02em;font-family:system-ui,-apple-system,sans-serif">
      Your company name
    </td>
  </tr>
</table>`;

const SAMPLE_FOOTER_HTML = `<table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="margin:0;border-top:1px solid #e2e8f0">
  <tr>
    <td style="padding:16px 12px;font-size:11px;color:#94a3b8;text-align:center;line-height:1.6;font-family:system-ui,-apple-system,sans-serif">
      Internal communication · ECDT Portal<br />
      <span style="color:#cbd5e1">This email was sent from the team announcements tool.</span>
    </td>
  </tr>
</table>`;

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const noOpLock = async (_name, _acquireTimeout, fn) => fn();
const announcementsSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, lock: noOpLock },
});

const FETCH_TIMEOUT_MS = 12000;

const COLUMNS = [
  { key: 'title', label: 'Title' },
  { key: 'sentAt', label: 'Sent' },
  { key: 'createdBy', label: 'Created by' },
  { key: 'sharedWith', label: 'Shared with' },
];

function formatAnnouncementDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

export default function TeamAnnouncements() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [profilesError, setProfilesError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templatesError, setTemplatesError] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [saving, setSaving] = useState(false);

  const headerEditorRef = useRef(null);
  const footerEditorRef = useRef(null);
  const [templateUiTab, setTemplateUiTab] = useState(TEMPLATE_TAB.visual);
  const [unlayerReady, setUnlayerReady] = useState(false);
  const [templateModalKey, setTemplateModalKey] = useState(0);
  /** True after user edits header/footer in HTML tab — save must not overwrite from Unlayer iframe. */
  const [templateCodeTouched, setTemplateCodeTouched] = useState(false);

  const [templateForm, setTemplateForm] = useState({
    name: '',
    title: '',
    body: '',
    headerHtml: '',
    footerHtml: '',
    headerDesign: null,
    footerDesign: null,
  });
  const [templateSaveError, setTemplateSaveError] = useState(null);
  const [templateSaveSuccess, setTemplateSaveSuccess] = useState(null);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [deletingTemplateId, setDeletingTemplateId] = useState(null);
  /** When set, Save updates this row instead of inserting. */
  const [editingTemplateId, setEditingTemplateId] = useState(null);

  const [announcements, setAnnouncements] = useState([]);
  const [announcementsError, setAnnouncementsError] = useState(null);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const [announcementSearch, setAnnouncementSearch] = useState('');
  const [announcementPageSize, setAnnouncementPageSize] = useState(10);
  const [announcementPage, setAnnouncementPage] = useState(1);

  const [form, setForm] = useState({
    title: '',
    body: '',
    emailHeaderHtml: '',
    emailFooterHtml: '',
    shareAllTeam: true,
    employeeIds: [],
  });

  const runWakeUp = useCallback(() => {
    return Promise.race([
      announcementsSupabase.from('profiles').select('id').limit(1),
      new Promise((_, reject) => setTimeout(() => reject(new Error('wakeup')), 5000)),
    ]).catch(() => {});
  }, []);

  const fetchProfiles = useCallback(async () => {
    setProfilesError(null);
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
      );
      const res = await Promise.race([
        announcementsSupabase.from('profiles').select('id, full_name, email').order('full_name', { ascending: true, nullsFirst: false }),
        timeoutPromise,
      ]);
      const { data, error } = res;
      if (error) {
        setProfilesError(error.message);
        setProfiles([]);
      } else {
        setProfiles(data ?? []);
      }
    } catch (e) {
      setProfilesError(e?.message || 'Failed to load');
      setProfiles([]);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    setTemplatesError(null);
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
      );
      const res = await Promise.race([
        announcementsSupabase
          .from('announcement_email_templates')
          .select(
            'id, name, title, body, header_html, footer_html, header_design_json, footer_design_json, created_at',
          )
          .order('name', { ascending: true }),
        timeoutPromise,
      ]);
      const { data, error } = res;
      if (error) {
        setTemplatesError(error.message);
        setTemplates([]);
      } else {
        setTemplates(data ?? []);
      }
    } catch (e) {
      setTemplatesError(e?.message || 'Failed to load templates');
      setTemplates([]);
    }
  }, []);

  const fetchAnnouncements = useCallback(async () => {
    setAnnouncementsError(null);
    setAnnouncementsLoading(true);
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
      );
      const res = await Promise.race([
        announcementsSupabase
          .from('announcements')
          .select(
            'id, title, created_at, share_all_team, shared_with_ids, emails_sent_count, emails_failed_count, created_by',
          )
          .order('created_at', { ascending: false }),
        timeoutPromise,
      ]);
      const { data, error } = res;
      if (error) {
        setAnnouncementsError(error.message);
        setAnnouncements([]);
      } else {
        setAnnouncements(data ?? []);
      }
    } catch (e) {
      setAnnouncementsError(e?.message || 'Failed to load announcements');
      setAnnouncements([]);
    } finally {
      setAnnouncementsLoading(false);
    }
  }, []);

  const refreshSessionAndFetch = useCallback(async () => {
    try {
      await announcementsSupabase.auth.getSession();
      await runWakeUp();
      await Promise.all([fetchProfiles(), fetchTemplates(), fetchAnnouncements()]);
    } catch (e) {
      setProfilesError(e?.message || 'Failed to load');
    }
  }, [runWakeUp, fetchProfiles, fetchTemplates, fetchAnnouncements]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { error: sessionErr } = await announcementsSupabase.auth.getSession();
      if (cancelled) return;
      if (sessionErr) {
        setProfilesError(sessionErr.message);
        return;
      }
      await runWakeUp();
      if (cancelled) return;
      await Promise.all([fetchProfiles(), fetchTemplates(), fetchAnnouncements()]);
    })();
    return () => { cancelled = true; };
  }, [runWakeUp, fetchProfiles, fetchTemplates, fetchAnnouncements]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refreshSessionAndFetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSessionAndFetch]);

  const activeColumnCount = COLUMNS.length + 1;

  const selectedEmployeeSet = useMemo(() => new Set(form.employeeIds), [form.employeeIds]);

  const profileNameById = useMemo(() => {
    const m = new Map();
    for (const p of profiles) {
      m.set(p.id, p.full_name || p.email || 'Unknown');
    }
    return m;
  }, [profiles]);

  const filteredAnnouncements = useMemo(() => {
    const q = announcementSearch.trim().toLowerCase();
    if (!q) return announcements;
    return announcements.filter((row) => {
      const t = (row.title || '').toLowerCase();
      const by = (profileNameById.get(row.created_by) || '').toLowerCase();
      return t.includes(q) || by.includes(q);
    });
  }, [announcements, announcementSearch, profileNameById]);

  const announcementTotalPages = Math.max(
    1,
    Math.ceil(filteredAnnouncements.length / announcementPageSize) || 1,
  );

  useEffect(() => {
    setAnnouncementPage((p) => Math.min(Math.max(1, p), announcementTotalPages));
  }, [announcementTotalPages]);

  useEffect(() => {
    setAnnouncementPage(1);
  }, [announcementSearch, announcementPageSize]);

  const paginatedAnnouncements = useMemo(() => {
    const start = (announcementPage - 1) * announcementPageSize;
    return filteredAnnouncements.slice(start, start + announcementPageSize);
  }, [filteredAnnouncements, announcementPage, announcementPageSize]);

  const announcementRangeLabel = useMemo(() => {
    const total = filteredAnnouncements.length;
    if (total === 0) return '0 / 0';
    const start = (announcementPage - 1) * announcementPageSize + 1;
    const end = Math.min(announcementPage * announcementPageSize, total);
    return `${start}–${end} / ${total}`;
  }, [filteredAnnouncements.length, announcementPage, announcementPageSize]);

  const resetNewTemplateForm = useCallback(() => {
    setTemplateCodeTouched(false);
    setTemplateModalKey((k) => k + 1);
    setEditingTemplateId(null);
    setTemplateForm({
      name: '',
      title: '',
      body: '',
      headerHtml: '',
      footerHtml: '',
      headerDesign: null,
      footerDesign: null,
    });
    setTemplateUiTab(TEMPLATE_TAB.visual);
    setTemplateSaveError(null);
    setTemplateSaveSuccess(null);
  }, []);

  const closeTemplateModal = useCallback(() => {
    setShowTemplateModal(false);
    setEditingTemplateId(null);
  }, []);

  const beginEditTemplate = useCallback((t) => {
    setTemplateCodeTouched(false);
    setTemplateModalKey((k) => k + 1);
    setTemplateForm({
      name: t.name ?? '',
      title: t.title ?? '',
      body: t.body ?? '',
      headerHtml: t.header_html ?? '',
      footerHtml: t.footer_html ?? '',
      headerDesign: t.header_design_json ?? null,
      footerDesign: t.footer_design_json ?? null,
    });
    setEditingTemplateId(t.id);
    setTemplateUiTab(TEMPLATE_TAB.visual);
    setTemplateSaveError(null);
    setTemplateSaveSuccess(null);
    setUnlayerReady(true);
    setShowTemplateModal(true);
  }, []);

  const openAdd = () => {
    setForm({
      title: '',
      body: '',
      emailHeaderHtml: '',
      emailFooterHtml: '',
      shareAllTeam: true,
      employeeIds: [],
    });
    setSelectedTemplateId('');
    setSaveError(null);
    setSaveSuccess(null);
    fetchTemplates();
    fetchAnnouncements();
    setShowAddModal(true);
  };

  const goTemplateTab = useCallback(
    async (next) => {
      if (templateUiTab === TEMPLATE_TAB.visual && next !== TEMPLATE_TAB.visual && unlayerReady) {
        try {
          const h = await exportFromEmailEditorRef(headerEditorRef);
          const f = await exportFromEmailEditorRef(footerEditorRef);
          setTemplateForm((prev) => ({
            ...prev,
            headerHtml: h.html || prev.headerHtml,
            footerHtml: f.html || prev.footerHtml,
            headerDesign: h.design ?? prev.headerDesign,
            footerDesign: f.design ?? prev.footerDesign,
          }));
          setTemplateCodeTouched(false);
        } catch {
          /* keep form as-is */
        }
      }
      setTemplateUiTab(next);
    },
    [templateUiTab, unlayerReady],
  );

  useEffect(() => {
    if (showTemplateModal && templateUiTab === TEMPLATE_TAB.visual) {
      setUnlayerReady(true);
    }
  }, [showTemplateModal, templateUiTab]);

  useEffect(() => {
    if (!showTemplateModal) {
      setUnlayerReady(false);
    }
  }, [showTemplateModal]);

  const openTemplateModal = () => {
    resetNewTemplateForm();
    fetchTemplates();
    setShowTemplateModal(true);
  };

  const toggleEmployee = (id) => {
    setForm((f) => {
      const next = new Set(f.employeeIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...f, employeeIds: Array.from(next) };
    });
  };

  const handleSaveTemplate = async () => {
    const name = templateForm.name.trim();
    if (!name) {
      setTemplateSaveError('Template name is required.');
      setTemplateSaveSuccess(null);
      return;
    }
    setTemplateSaving(true);
    setTemplateSaveError(null);
    setTemplateSaveSuccess(null);
    try {
      let headerHtmlOut = templateForm.headerHtml.trim();
      let footerHtmlOut = templateForm.footerHtml.trim();
      let headerDesignOut = templateForm.headerDesign;
      let footerDesignOut = templateForm.footerDesign;
      const useFormHtmlOnly =
        templateUiTab === TEMPLATE_TAB.code ||
        (templateCodeTouched && templateUiTab !== TEMPLATE_TAB.visual);
      if (!useFormHtmlOnly && unlayerReady && headerEditorRef.current?.editor) {
        const h = await exportFromEmailEditorRef(headerEditorRef);
        headerHtmlOut = (h.html || headerHtmlOut).trim();
        headerDesignOut = h.design ?? headerDesignOut;
      }
      if (!useFormHtmlOnly && unlayerReady && footerEditorRef.current?.editor) {
        const f = await exportFromEmailEditorRef(footerEditorRef);
        footerHtmlOut = (f.html || footerHtmlOut).trim();
        footerDesignOut = f.design ?? footerDesignOut;
      }

      const { data: sessionRes } = await announcementsSupabase.auth.getSession();
      const uid = sessionRes?.session?.user?.id;
      if (!uid) {
        setTemplateSaveError('You must be signed in to save a template.');
        return;
      }

      const payload = {
        name,
        title: templateForm.title.trim(),
        body: templateForm.body.trim(),
        header_html: headerHtmlOut,
        footer_html: footerHtmlOut,
        header_design_json: headerDesignOut,
        footer_design_json: footerDesignOut,
      };

      if (editingTemplateId) {
        const { error } = await announcementsSupabase
          .from('announcement_email_templates')
          .update(payload)
          .eq('id', editingTemplateId);
        if (error) {
          setTemplateSaveError(error.message);
          return;
        }
        setTemplateSaveSuccess('Template updated.');
      } else {
        const { error } = await announcementsSupabase.from('announcement_email_templates').insert({
          ...payload,
          created_by: uid,
        });
        if (error) {
          setTemplateSaveError(error.message);
          return;
        }
        setTemplateSaveSuccess('Template saved.');
        resetNewTemplateForm();
      }
      await fetchTemplates();
    } catch (e) {
      setTemplateSaveError(e?.message || 'Failed to save template.');
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    setDeletingTemplateId(id);
    setTemplateSaveError(null);
    try {
      const { error } = await announcementsSupabase.from('announcement_email_templates').delete().eq('id', id);
      if (error) {
        setTemplateSaveError(error.message);
        return;
      }
      if (editingTemplateId === id) {
        resetNewTemplateForm();
      }
      await fetchTemplates();
      setSelectedTemplateId((cur) => (cur === id ? '' : cur));
    } catch (e) {
      setTemplateSaveError(e?.message || 'Failed to delete template.');
    } finally {
      setDeletingTemplateId(null);
    }
  };

  const handleSaveAnnouncement = async () => {
    const title = form.title.trim();
    const body = form.body.trim();
    if (!title) {
      setSaveError('Title is required.');
      setSaveSuccess(null);
      return;
    }
    if (!body) {
      setSaveError('Announcement body is required.');
      setSaveSuccess(null);
      return;
    }

    const targetProfiles = form.shareAllTeam
      ? profiles
      : profiles.filter((p) => selectedEmployeeSet.has(p.id));
    const recipients = Array.from(
      new Set(targetProfiles.map((p) => (p.email || '').trim()).filter(Boolean)),
    );

    if (!recipients.length) {
      setSaveError('No recipient emails found for selected employees.');
      setSaveSuccess(null);
      return;
    }

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(null);
    try {
      const { data: sessionRes } = await announcementsSupabase.auth.getSession();
      const sender =
        sessionRes?.session?.user?.user_metadata?.full_name ||
        sessionRes?.session?.user?.email ||
        'Team';
      const subject = `New announcement: ${title}`;
      const html = buildProfessionalAnnouncementHtml({
        sender,
        title,
        body,
        headerHtml: form.emailHeaderHtml,
        footerHtml: form.emailFooterHtml,
      });
      const textBody = buildAnnouncementPlainText({
        sender,
        title,
        body,
        headerHtml: form.emailHeaderHtml,
        footerHtml: form.emailFooterHtml,
      });

      const results = await Promise.all(
        recipients.map((to) => sendEmail({ to, subject, body: textBody, html })),
      );
      const sentCount = results.filter((r) => r.success).length;
      const failedCount = results.length - sentCount;

      if (sentCount === 0) {
        setSaveError(results.find((r) => !r.success)?.error || 'Failed to send announcement emails.');
        return;
      }

      const uid = sessionRes?.session?.user?.id;
      if (uid) {
        const { error: logErr } = await announcementsSupabase.from('announcements').insert({
          title,
          body,
          email_header_html: form.emailHeaderHtml.trim(),
          email_footer_html: form.emailFooterHtml.trim(),
          share_all_team: form.shareAllTeam,
          shared_with_ids: form.shareAllTeam ? [] : [...form.employeeIds],
          emails_sent_count: sentCount,
          emails_failed_count: failedCount,
          created_by: uid,
        });
        if (logErr) {
          console.error('announcements insert', logErr);
        }
      }
      await fetchAnnouncements();

      setSaveSuccess(
        failedCount
          ? `Announcement emailed to ${sentCount}/${results.length} recipients.`
          : `Announcement emailed to ${sentCount} recipient${sentCount === 1 ? '' : 's'}.`,
      );
      setShowAddModal(false);
      setSelectedTemplateId('');
      setForm({
        title: '',
        body: '',
        emailHeaderHtml: '',
        emailFooterHtml: '',
        shareAllTeam: true,
        employeeIds: [],
      });
    } catch (e) {
      setSaveError(e?.message || 'Failed to send announcement emails.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tann-page">
      <div className="tann-card">
        <div className="tann-header-row">
          <h1 className="tann-title">Announcements</h1>
          <div className="tann-header-actions">
            <button type="button" className="tann-header-btn" onClick={openTemplateModal}>
              Add template
            </button>
            <button
              type="button"
              className="tann-header-btn tann-header-btn-primary"
              onClick={openAdd}
            >
              <IconPlus />
              Add announcement
            </button>
          </div>
        </div>

        <div className="tann-filters-row">
          <div className="tann-filters-left">
            <button
              type="button"
              className="tann-layout-toggle"
              aria-label="Layout options"
            >
              ≡
            </button>
          </div>

          <div className="tann-filters-right">
            <div className="tann-search">
              <input
                type="text"
                placeholder="Search title or sender"
                aria-label="Search announcements"
                value={announcementSearch}
                onChange={(e) => setAnnouncementSearch(e.target.value)}
              />
              <span className="tann-search-icon">
                <IconSearch />
              </span>
            </div>
          </div>
        </div>

        {announcementsError && (
          <p className="tann-announcements-banner" role="alert">
            {announcementsError}
          </p>
        )}

        <div className="tann-table-wrapper">
          <table className="tann-table">
            <thead>
              <tr>
                {COLUMNS.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {announcementsLoading ? (
                <tr>
                  <td className="tann-table-empty" colSpan={activeColumnCount}>
                    Loading…
                  </td>
                </tr>
              ) : paginatedAnnouncements.length === 0 ? (
                <tr>
                  <td className="tann-table-empty" colSpan={activeColumnCount}>
                    No announcements yet. Send one with &quot;Add announcement&quot;.
                  </td>
                </tr>
              ) : (
                paginatedAnnouncements.map((row) => (
                  <tr key={row.id}>
                    <td className="tann-cell-title" title={row.title}>
                      {row.title}
                    </td>
                    <td className="tann-cell-muted">{formatAnnouncementDate(row.created_at)}</td>
                    <td>{profileNameById.get(row.created_by) || '—'}</td>
                    <td className="tann-cell-shared">
                      {row.share_all_team
                        ? 'All team members'
                        : (row.shared_with_ids || [])
                            .map((id) => profileNameById.get(id) || '—')
                            .join(', ') || '—'}
                    </td>
                    <td />
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="tann-footer-row">
          <div className="tann-footer-left">
            <select
              className="tann-page-size"
              value={announcementPageSize}
              aria-label="Rows per page"
              onChange={(e) => setAnnouncementPageSize(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span>{announcementRangeLabel}</span>
          </div>
          <div className="tann-footer-right">
            <button
              type="button"
              className="tann-page-btn"
              aria-label="Previous page"
              disabled={announcementPage <= 1}
              onClick={() => setAnnouncementPage((p) => Math.max(1, p - 1))}
            >
              «
            </button>
            <button
              type="button"
              className="tann-page-btn tann-page-btn-active"
              aria-label="Current page"
              disabled
            >
              {announcementPage}
            </button>
            <button
              type="button"
              className="tann-page-btn"
              aria-label="Next page"
              disabled={announcementPage >= announcementTotalPages}
              onClick={() =>
                setAnnouncementPage((p) => Math.min(announcementTotalPages, p + 1))
              }
            >
              »
            </button>
          </div>
        </div>
      </div>

      {showAddModal && (
        <div
          className="estimates-modal-backdrop tann-announcement-modal-backdrop"
          onClick={() => setShowAddModal(false)}
        >
          <div
            className="estimates-modal tann-modal-wide tann-announcement-modal-panel"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="estimates-modal-header">
              <h2 className="estimates-modal-title">Add announcement</h2>
              <button
                type="button"
                className="estimates-modal-close"
                onClick={() => setShowAddModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="estimates-modal-body">
              <div className="tann-form">
                <div className="tann-form-row">
                  <label className="tann-form-label" htmlFor="announcement-template-select">
                    Template
                  </label>
                  <select
                    id="announcement-template-select"
                    className="tann-input"
                    value={selectedTemplateId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setSelectedTemplateId(id);
                      if (!id) return;
                      const t = templates.find((x) => x.id === id);
                      if (t) {
                        setForm((f) => ({
                          ...f,
                          title: t.title ?? '',
                          body: t.body ?? '',
                          emailHeaderHtml: t.header_html ?? '',
                          emailFooterHtml: t.footer_html ?? '',
                        }));
                      }
                    }}
                  >
                    <option value="">Load from saved template…</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  {templatesError && (
                    <p className="tann-inline-hint tann-inline-hint-warn" role="status">
                      {templatesError}
                    </p>
                  )}
                </div>

                <div className="tann-form-row tann-form-row-align-start">
                  <label className="tann-form-label" htmlFor="announcement-email-header">
                    Email header (HTML)
                  </label>
                  <textarea
                    id="announcement-email-header"
                    className="tann-input tann-code-field"
                    rows={4}
                    spellCheck={false}
                    placeholder="Optional — appears above the message (HTML)."
                    value={form.emailHeaderHtml}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, emailHeaderHtml: e.target.value }))
                    }
                  />
                </div>

                <div className="tann-form-row tann-form-row-align-start">
                  <label className="tann-form-label" htmlFor="announcement-email-footer">
                    Email footer (HTML)
                  </label>
                  <textarea
                    id="announcement-email-footer"
                    className="tann-input tann-code-field"
                    rows={5}
                    spellCheck={false}
                    placeholder="Optional — appears below the message (HTML)."
                    value={form.emailFooterHtml}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, emailFooterHtml: e.target.value }))
                    }
                  />
                </div>

                <div className="tann-form-row">
                  <label className="tann-form-label">Title</label>
                  <input
                    className="tann-input"
                    placeholder="Subject line title (plain text)"
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  />
                </div>

                <div className="tann-form-row tann-form-row-align-start">
                  <label className="tann-form-label" htmlFor="announcement-main-body">
                    Main content
                  </label>
                  <p className="tann-field-hint">
                    Plain text or HTML for the main message (line breaks kept for plain text).
                  </p>
                  <textarea
                    id="announcement-main-body"
                    className="tann-input tann-code-field tann-editor-area-min"
                    value={form.body}
                    spellCheck={true}
                    onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                  />
                </div>

                <div className="tann-share-row">
                  <span className="tann-form-label">Share with</span>
                  <label>
                    <input
                      type="checkbox"
                      checked={form.shareAllTeam}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          shareAllTeam: e.target.checked,
                          employeeIds: e.target.checked ? [] : f.employeeIds,
                        }))
                      }
                    />{' '}
                    All team members
                  </label>
                </div>

                {!form.shareAllTeam && (
                  <div className="tann-employee-picker">
                    <div className="tann-employee-picker-head">
                      <span className="tann-form-label">Select employees</span>
                      <span className="tann-employee-picker-meta">
                        {form.employeeIds.length} selected
                      </span>
                    </div>

                    {profilesError ? (
                      <p className="tann-employee-error" role="alert">
                        {profilesError}
                      </p>
                    ) : (
                      <div className="tann-employee-list">
                        {profiles.length === 0 ? (
                          <div className="tann-employee-empty">No employees found.</div>
                        ) : (
                          profiles.map((p) => (
                            <label key={p.id} className="tann-employee-item">
                              <input
                                type="checkbox"
                                checked={selectedEmployeeSet.has(p.id)}
                                onChange={() => toggleEmployee(p.id)}
                              />{' '}
                              <span className="tann-employee-name">{p.full_name || 'Unnamed'}</span>
                            </label>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}

                {saveError && (
                  <p className="salary-form-error salary-form-error-inline" role="alert">
                    {saveError}
                  </p>
                )}
                {saveSuccess && (
                  <p className="login-message" style={{ marginTop: '0.5rem' }}>
                    {saveSuccess}
                  </p>
                )}
              </div>
            </div>

            <div className="estimates-modal-footer">
              <button
                type="button"
                className="estimates-footer-btn"
                onClick={() => !saving && setShowAddModal(false)}
                disabled={saving}
              >
                Close
              </button>
              <button
                type="button"
                className="estimates-footer-btn estimates-footer-btn-primary"
                onClick={handleSaveAnnouncement}
                disabled={saving}
              >
                {saving ? 'Sending…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTemplateModal && (
        <div
          className="estimates-modal-backdrop tann-announcement-modal-backdrop"
          onClick={() => !templateSaving && !deletingTemplateId && closeTemplateModal()}
        >
          <div
            className="estimates-modal tann-modal-wide tann-modal-templates tann-announcement-modal-panel"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="estimates-modal-header tann-template-modal-header">
              <div className="tann-template-modal-title-wrap">
                <h2 className="estimates-modal-title" id="email-templates-modal-title">
                  {editingTemplateId ? 'Edit template' : 'Email templates'}
                </h2>
                {editingTemplateId && (
                  <button
                    type="button"
                    className="tann-template-new-link"
                    disabled={!!templateSaving || !!deletingTemplateId}
                    onClick={() => {
                      resetNewTemplateForm();
                      fetchTemplates();
                    }}
                  >
                    New template
                  </button>
                )}
              </div>
              <button
                type="button"
                className="estimates-modal-close"
                onClick={() => !templateSaving && !deletingTemplateId && closeTemplateModal()}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="estimates-modal-body tann-template-modal-body">
              <div className="tann-form">
                <p className="tann-template-intro tann-template-intro-compact">
                  Design a <strong>header</strong> and <strong>footer</strong> with drag-and-drop, or paste HTML. They wrap
                  the announcement card when you send mail. HR and admin only.
                </p>

                <div className="tann-form-row">
                  <label className="tann-form-label" htmlFor="tmpl-name">
                    Template name
                  </label>
                  <input
                    id="tmpl-name"
                    className="tann-input"
                    placeholder="e.g. Quarterly update"
                    value={templateForm.name}
                    onChange={(e) =>
                      setTemplateForm((f) => ({ ...f, name: e.target.value }))
                    }
                  />
                </div>

                <div className="tann-template-tablist" role="tablist" aria-label="Template sections">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={templateUiTab === TEMPLATE_TAB.visual}
                    className={
                      templateUiTab === TEMPLATE_TAB.visual
                        ? 'tann-template-tab tann-template-tab-active'
                        : 'tann-template-tab'
                    }
                    onClick={() => goTemplateTab(TEMPLATE_TAB.visual)}
                  >
                    Drag &amp; drop
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={templateUiTab === TEMPLATE_TAB.code}
                    className={
                      templateUiTab === TEMPLATE_TAB.code
                        ? 'tann-template-tab tann-template-tab-active'
                        : 'tann-template-tab'
                    }
                    onClick={() => goTemplateTab(TEMPLATE_TAB.code)}
                  >
                    HTML
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={templateUiTab === TEMPLATE_TAB.defaults}
                    className={
                      templateUiTab === TEMPLATE_TAB.defaults
                        ? 'tann-template-tab tann-template-tab-active'
                        : 'tann-template-tab'
                    }
                    onClick={() => goTemplateTab(TEMPLATE_TAB.defaults)}
                  >
                    Defaults
                  </button>
                </div>

                {unlayerReady && (
                  <div
                    className="tann-template-tabpanel"
                    role="tabpanel"
                    hidden={templateUiTab !== TEMPLATE_TAB.visual}
                    style={{
                      display: templateUiTab === TEMPLATE_TAB.visual ? 'block' : 'none',
                    }}
                  >
                    <AnnouncementUnlayerEditors
                      key={templateModalKey}
                      headerRef={headerEditorRef}
                      footerRef={footerEditorRef}
                      headerDesign={templateForm.headerDesign}
                      footerDesign={templateForm.footerDesign}
                    />
                  </div>
                )}

                {templateUiTab === TEMPLATE_TAB.code && (
                  <div className="tann-template-tabpanel" role="tabpanel">
                    <p className="tann-field-hint">
                      Paste or edit raw HTML. Saving uses this text instead of the drag-and-drop canvas while you stay on
                      this tab.
                    </p>
                    <div className="tann-form-row tann-form-row-align-start">
                      <div className="tann-label-row">
                        <label className="tann-form-label" htmlFor="tmpl-header-html">
                          Header HTML
                        </label>
                        <button
                          type="button"
                          className="tann-sample-btn"
                          onClick={() => {
                            setTemplateCodeTouched(true);
                            setTemplateForm((f) => ({
                              ...f,
                              headerHtml: SAMPLE_HEADER_HTML,
                              footerHtml: f.footerHtml || SAMPLE_FOOTER_HTML,
                            }));
                          }}
                        >
                          Insert sample
                        </button>
                      </div>
                      <textarea
                        id="tmpl-header-html"
                        className="tann-input tann-code-field"
                        rows={7}
                        spellCheck={false}
                        value={templateForm.headerHtml}
                        onChange={(e) => {
                          setTemplateCodeTouched(true);
                          setTemplateForm((f) => ({ ...f, headerHtml: e.target.value }));
                        }}
                      />
                    </div>

                    <div className="tann-form-row tann-form-row-align-start">
                      <label className="tann-form-label" htmlFor="tmpl-footer-html">
                        Footer HTML
                      </label>
                      <textarea
                        id="tmpl-footer-html"
                        className="tann-input tann-code-field"
                        rows={6}
                        spellCheck={false}
                        value={templateForm.footerHtml}
                        onChange={(e) => {
                          setTemplateCodeTouched(true);
                          setTemplateForm((f) => ({ ...f, footerHtml: e.target.value }));
                        }}
                      />
                    </div>
                  </div>
                )}

                {templateUiTab === TEMPLATE_TAB.defaults && (
                  <div className="tann-template-tabpanel" role="tabpanel">
                    <div className="tann-form-row">
                      <label className="tann-form-label">Default announcement title</label>
                      <input
                        className="tann-input"
                        placeholder="Optional — filled when you load this template"
                        value={templateForm.title}
                        onChange={(e) =>
                          setTemplateForm((f) => ({ ...f, title: e.target.value }))
                        }
                      />
                    </div>

                    <div className="tann-form-row tann-form-row-align-start">
                      <label className="tann-form-label" htmlFor="tmpl-body-html">
                        Default main content
                      </label>
                      <p className="tann-field-hint">HTML or plain text for the message body (optional).</p>
                      <textarea
                        id="tmpl-body-html"
                        className="tann-input tann-code-field tann-template-body"
                        rows={8}
                        spellCheck={true}
                        placeholder="Optional starter paragraphs or list markup."
                        value={templateForm.body}
                        onChange={(e) =>
                          setTemplateForm((f) => ({ ...f, body: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                )}

                {templateSaveError && (
                  <p className="salary-form-error salary-form-error-inline" role="alert">
                    {templateSaveError}
                  </p>
                )}
                {templateSaveSuccess && (
                  <p className="login-message" style={{ marginTop: '0.5rem' }}>
                    {templateSaveSuccess}
                  </p>
                )}

                <div className="tann-template-saved">
                  <div className="tann-template-saved-head">
                    <span className="tann-form-label">Saved templates</span>
                    {templatesError && (
                      <span className="tann-inline-hint tann-inline-hint-warn">{templatesError}</span>
                    )}
                  </div>
                  {templates.length === 0 && !templatesError ? (
                    <p className="tann-template-empty">No templates yet.</p>
                  ) : (
                    <ul className="tann-template-list">
                      {templates.map((t) => (
                        <li
                          key={t.id}
                          className={
                            editingTemplateId === t.id
                              ? 'tann-template-item tann-template-item-active'
                              : 'tann-template-item'
                          }
                        >
                          <span className="tann-template-item-name" title={t.name}>
                            {t.name}
                          </span>
                          <div className="tann-template-item-actions">
                            <button
                              type="button"
                              className="tann-template-edit"
                              disabled={!!deletingTemplateId || !!templateSaving}
                              onClick={() => beginEditTemplate(t)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="tann-template-delete"
                              disabled={!!deletingTemplateId}
                              onClick={() => handleDeleteTemplate(t.id)}
                            >
                              {deletingTemplateId === t.id ? 'Removing…' : 'Delete'}
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>

            <div className="estimates-modal-footer">
              <button
                type="button"
                className="estimates-footer-btn"
                onClick={() => !templateSaving && !deletingTemplateId && closeTemplateModal()}
                disabled={templateSaving}
              >
                Close
              </button>
              <button
                type="button"
                className="estimates-footer-btn estimates-footer-btn-primary"
                onClick={handleSaveTemplate}
                disabled={templateSaving || !!deletingTemplateId}
              >
                {templateSaving ? 'Saving…' : editingTemplateId ? 'Update template' : 'Save template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

